import React, { useState } from "react";
import { Stack, Modal, Button as MuiButton } from "@mui/material";
import ImagePromptModal from "./Modals/ImagePromptModal";
import Button from "./Button";
import AIButton from "./AIButton";
import FolderIcon from "../img/Folder.svg";
import { useFilePicker } from "use-file-picker";
import {
    FileAmountLimitValidator,
    FileTypeValidator,
    FileSizeValidator,
} from "use-file-picker/validators";
import Color from "../modules/Color";

interface BaseProps {
    text?: string;
    isText?: boolean;
    onUpload: (val?: string) => void;
    customColor?: string;
    shouldPulse?: boolean;
    style?: string;
    size?: "512x512" | "1024x1024";
}

interface WithAIProps extends BaseProps {
    withAI: true;
    brandName: string;
    businessType: string;
    brandColor: Color;
}

interface WithoutAIProps extends BaseProps {
    withAI?: false; // or just omit
    brandName?: string;
    businessType?: string;
    brandColor?: Color;
}

type Props = WithAIProps | WithoutAIProps;

const ImagePicker = (props: Props) => {
    const [openModal, setOpenModal] = useState(false);

    const { text, onUpload } = props;
    const isText = props.isText ?? false;

    const onCancel = () => {
        setOpenModal(false);
    };

    const onSave = (val?: string) => {
        onCancel();
        if (!val) return;
        onUpload(val);
    };

    const { openFilePicker, loading } = useFilePicker({
        readAs: "DataURL",
        accept: "image/*",
        multiple: false,
        validators: [
            new FileAmountLimitValidator({ max: 1 }),
            new FileTypeValidator(["jpeg", "jpg", "png", "heic", "heif"]),
            new FileSizeValidator({ maxFileSize: 10 * 1024 * 1024 }), // 10 MB
        ],
        onFilesSelected: ({ errors }) => {
            if (errors) {
                console.error("File upload error:", errors);
            }
        },
        onFilesSuccessfullySelected: ({ filesContent }) => {
            const item = filesContent[0];
            if (item) {
                onUpload(item.content);
            }
        },
    });

    return (
        <Stack spacing={2}>
            {isText ? (
                <MuiButton
                    variant="text"
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "14px",
                        lineHeight: "18.2px",
                    }}
                    onClick={openFilePicker}
                    disabled={loading}
                >
                    {text ?? "Change photo"}
                </MuiButton>
            ) : props.withAI ? (
                <Stack direction="row" spacing={1.5} justifyContent="center">
                    <Button
                        variant="medium"
                        color="transparent"
                        customColor={props.customColor}
                        icon={FolderIcon}
                        onClick={openFilePicker}
                        disabled={loading}
                    >
                        {text ?? "Upload"}
                    </Button>
                    <AIButton
                        aiButtonId="image-picker-generate"
                        variant="medium"
                        color="primary"
                        customColor={props.customColor}
                        onClick={() => setOpenModal(true)}
                        disabled={loading}
                        enabled={props.shouldPulse}
                    >
                        Generate
                    </AIButton>
                </Stack>
            ) : (
                <Button
                    variant="medium"
                    color="transparent"
                    customColor={props.customColor}
                    icon={FolderIcon}
                    onClick={openFilePicker}
                    disabled={loading}
                >
                    {text ?? "Upload Icon"}
                </Button>
            )}

            {props.withAI && (
                <Modal open={openModal} onClose={() => {}}>
                    <ImagePromptModal
                        open={openModal}
                        onCancel={onCancel}
                        onSave={onSave}
                        brandName={props.brandName}
                        businessType={props.businessType}
                        color={props.brandColor}
                    />
                </Modal>
            )}
        </Stack>
    );
};

export default ImagePicker;
